/**
 * Plugin lifecycle: contract validation, ordering, and hook invocation.
 *
 * See library/swiper/Swiper-Architecture.md for the full plugin contract. In short, every
 * feature exports a SwiperPlugin object with optional hooks; the initializer
 * threads them through a deterministic two-pass pipeline.
 *
 * @typedef {Object} PluginContext
 * @property {HTMLElement} section              The outer .swiper-section we're iterating.
 * @property {HTMLElement} parentSwiperSection  Closest .swiper-section to the swiper element
 *                                              (equals `section` unless the swiper is nested).
 * @property {Object} registry                  The shared SwiperRegistry instance.
 * @property {Array} sectionSwipers             Swipers built so far in this section.
 * @property {Object} initializer               The Initializer instance. Plugins use it for
 *                                              `rebuildSection(section)` and the shared
 *                                              `onResize(handler)` bus (see "Don't attach
 *                                              your own `window.resize` listener" pitfall
 *                                              in library/swiper/Swiper-Architecture.md).
 *
 * @typedef {Object} GateHelpers
 * @property {Function} Swiper                    Swiper constructor.
 * @property {SwiperPlugin[]} plugins             Full plugin list (sorted).
 * @property {(el: HTMLElement, parentSwiperSection: HTMLElement) => any} initSwiper
 *           Build one swiper through the full plugin pipeline (parseOptions
 *           → new Swiper → afterInit → afterAllInit). Used by gate plugins
 *           that lazy-create swipers (e.g. tab-system on tab activation).
 *           The created swiper is added to the section's swiper list and
 *           registered in the section.id-keyed lookup automatically.
 *
 * @typedef {Object} SwiperPlugin
 * @property {string} name
 * @property {string[]} [runsAfter]
 * @property {(el: HTMLElement, section: HTMLElement) => boolean} [matches]
 * @property {(section: HTMLElement) => boolean} [gateInit]
 *           PREDICATE only — called with just (section) to test whether this
 *           plugin claims the section. MUST be a pure predicate with no side
 *           effects. If true, the initializer skips its normal pipeline and
 *           hands off to `runGate`.
 * @property {(section: HTMLElement, ctx: PluginContext, helpers: GateHelpers) => void} [runGate]
 *           Handler for gated sections. Called once per section after
 *           `gateInit` returns true. The plugin owns the section's lifecycle
 *           from here — wiring UI, lazily creating swipers via
 *           `helpers.initSwiper`, and registering the section with the
 *           registry to avoid re-init.
 * @property {(el, section, options, ctx: PluginContext) => Object} [parseOptions]
 * @property {(swiper, ctx: PluginContext) => void} [afterInit]
 * @property {(swiper, ctx: PluginContext) => void} [afterAllInit]
 * @property {(swiper, ctx: PluginContext) => void} [onDestroy]
 */

function assertValidPlugin(plugin) {
  if (!plugin || typeof plugin !== 'object') {
    throw new Error('Swiper plugin must be an object');
  }
  if (typeof plugin.name !== 'string' || !plugin.name) {
    throw new Error('Swiper plugin must have a non-empty `name`');
  }
  if (plugin.runsAfter != null && !Array.isArray(plugin.runsAfter)) {
    throw new Error(`Swiper plugin "${plugin.name}": runsAfter must be an array`);
  }
}

/**
 * Topologically sort plugins so that any plugin listed in another plugin's
 * `runsAfter` is placed earlier in the resulting list. Ties (no dependency
 * between plugins) are broken by registration order.
 *
 * Throws on:
 *   - duplicate plugin names
 *   - runsAfter entries that don't match any registered plugin
 *   - cycles
 *
 * @param {SwiperPlugin[]} plugins
 * @returns {SwiperPlugin[]}
 */
export function sortPlugins(plugins) {
  plugins.forEach(assertValidPlugin);

  const byName = new Map();
  for (const p of plugins) {
    if (byName.has(p.name)) {
      throw new Error(`Duplicate swiper plugin name: "${p.name}"`);
    }
    byName.set(p.name, p);
  }

  for (const p of plugins) {
    for (const dep of p.runsAfter || []) {
      if (!byName.has(dep)) {
        throw new Error(
          `Swiper plugin "${p.name}" runsAfter unknown plugin "${dep}"`
        );
      }
    }
  }

  // Kahn's algorithm. To make ties deterministic by registration order, we
  // scan the original `plugins` array each round to find the next zero-indegree
  // candidate, rather than using an unordered queue.
  const remaining = new Set(plugins.map((p) => p.name));
  const inDegree = new Map();
  for (const p of plugins) {
    inDegree.set(p.name, (p.runsAfter || []).length);
  }

  const sorted = [];
  while (remaining.size > 0) {
    let progressed = false;
    for (const p of plugins) {
      if (!remaining.has(p.name)) continue;
      if (inDegree.get(p.name) !== 0) continue;

      sorted.push(p);
      remaining.delete(p.name);
      progressed = true;

      for (const other of plugins) {
        if (!remaining.has(other.name)) continue;
        if ((other.runsAfter || []).includes(p.name)) {
          inDegree.set(other.name, inDegree.get(other.name) - 1);
        }
      }
      break;
    }

    if (!progressed) {
      const cycle = [...remaining].join(', ');
      throw new Error(`Swiper plugin dependency cycle involving: ${cycle}`);
    }
  }

  return sorted;
}

/**
 * Filter sorted plugins to only those whose `matches(el, section)` returns
 * truthy. Plugins without a `matches` function always match.
 *
 * @param {SwiperPlugin[]} sortedPlugins
 * @param {HTMLElement} el       The .swiper element under construction.
 * @param {HTMLElement} section  The closest .swiper-section to `el`.
 * @returns {SwiperPlugin[]}
 */
export function applicablePlugins(sortedPlugins, el, section) {
  return sortedPlugins.filter((p) => {
    if (typeof p.matches !== 'function') return true;
    try {
      return !!p.matches(el, section);
    } catch (err) {
      console.warn(`Swiper plugin "${p.name}" matches threw:`, err);
      return false;
    }
  });
}

/**
 * Build a short identifier for a section element to include in error logs.
 * Prefers `section.id`; falls back to a class-based hint so a thrown plugin
 * is traceable to a specific section without the developer needing to
 * reproduce locally with the same Liquid content. Safe on missing/null
 * inputs.
 */
function describeSection(section) {
  if (!section) return '<no-section>';
  if (section.id) return `#${section.id}`;
  const cls = (section.className || '').split(/\s+/).filter(Boolean)[0];
  return cls ? `.${cls}` : (section.tagName || 'unknown').toLowerCase();
}

/**
 * Best-effort section extraction from the trailing ctx argument that every
 * hook receives. Returns null if ctx isn't shaped like a PluginContext.
 */
function sectionFromCtx(ctx) {
  if (!ctx || typeof ctx !== 'object') return null;
  return ctx.section || ctx.parentSwiperSection || null;
}

/**
 * Run a side-effecting hook (afterInit / afterAllInit / onDestroy) across
 * a list of plugins in order. Errors thrown by one plugin are logged and
 * swallowed so other plugins still run.
 *
 * @param {SwiperPlugin[]} plugins
 * @param {string} hookName
 * @param  {...any} args
 */
export function runHook(plugins, hookName, ...args) {
  // Last arg is conventionally the PluginContext (afterInit/afterAllInit/
  // onDestroy all pass `(swiper, ctx)`). Pull section context for logs;
  // never throws and never alters args.
  const sectionLabel = describeSection(sectionFromCtx(args[args.length - 1]));
  for (const p of plugins) {
    const fn = p[hookName];
    if (typeof fn !== 'function') continue;
    try {
      fn.apply(p, args);
    } catch (err) {
      console.error(
        `Swiper plugin "${p.name}" failed in "${hookName}" (section ${sectionLabel}):`,
        err
      );
    }
  }
}

/**
 * Special hook runner for parseOptions: each plugin receives the running
 * options object and may return a modified one (or mutate in place).
 *
 * @param {SwiperPlugin[]} plugins
 * @param {HTMLElement} el
 * @param {HTMLElement} section
 * @param {Object} initialOptions
 * @param {PluginContext} ctx
 * @returns {Object}
 */
export function runParseOptions(plugins, el, section, initialOptions, ctx) {
  let options = initialOptions;
  const sectionLabel = describeSection(section);
  for (const p of plugins) {
    if (typeof p.parseOptions !== 'function') continue;
    try {
      const result = p.parseOptions(el, section, options, ctx);
      if (result && typeof result === 'object') {
        options = result;
      }
    } catch (err) {
      console.error(
        `Swiper plugin "${p.name}" failed in "parseOptions" (section ${sectionLabel}):`,
        err
      );
    }
  }
  return options;
}

/**
 * Find the first plugin (in priority order) whose `gateInit(section)` returns
 * truthy. The initializer hands the section over to that plugin and skips its
 * normal pipeline. Used by tab-system.
 *
 * @param {SwiperPlugin[]} plugins
 * @param {HTMLElement} section
 * @returns {SwiperPlugin | null}
 */
export function findGate(plugins, section) {
  const sectionLabel = describeSection(section);
  for (const p of plugins) {
    if (typeof p.gateInit !== 'function') continue;
    try {
      if (p.gateInit(section)) return p;
    } catch (err) {
      console.warn(
        `Swiper plugin "${p.name}" gateInit threw (section ${sectionLabel}):`,
        err
      );
    }
  }
  return null;
}
